import type { PriceGroup } from '@adapters/bigcommerce/types/product';
import { usePdp } from '@context/PdpContext';
import usePrice from '@hooks/usePrice';

export interface PdpOriginalPrice {
  /** The struck-through price to render, or null when there is none */
  originalPriceString: string | null;
  isMemberDiscount: boolean;
}

/**
 * Resolves the PDP's struck-through "original" price: `useFormattedPrice`'s
 * sale/clearance or guest-comparison original, falling back to the member-discount
 * original (guest price, else base price) for non-sale/clearance member pricing.
 *
 * Shared by the buy zone price and the sticky ATC bar so the two can't drift apart.
 */
const usePdpOriginalPrice = (
  price: PriceGroup | null,
  formattedOriginalPriceString: string | null
): PdpOriginalPrice => {
  const {
    promoBadge,
    hasMemberPriceDifference,
    customerGroupPrices,
    selectedVariant,
    actualPriceList,
  } = usePdp();
  const formatPrice = usePrice();

  const currentPriceValue = price?.price?.value ?? price?.lowPrice?.value;
  const originalPriceValue =
    price?.basePrice?.value ?? price?.baseLowPrice?.value;

  const guestPrice = selectedVariant
    ? customerGroupPrices?.guest?.variants.find(
        (variant) => variant.entityId === selectedVariant.entityId
      )?.price
    : customerGroupPrices?.guest?.price;
  const guestOriginalPriceValue =
    guestPrice?.price?.value ?? guestPrice?.lowPrice?.value;

  const isMemberDiscount =
    hasMemberPriceDifference &&
    actualPriceList.groupKey !== 'guest' &&
    promoBadge?.code !== 'CLEARANCE' &&
    promoBadge?.code !== 'SALE';
  const memberOriginalPriceString =
    isMemberDiscount &&
    guestOriginalPriceValue &&
    currentPriceValue &&
    guestOriginalPriceValue > currentPriceValue
      ? formatPrice(guestOriginalPriceValue)
      : isMemberDiscount && originalPriceValue
      ? formatPrice(originalPriceValue)
      : null;

  return {
    originalPriceString:
      formattedOriginalPriceString ?? memberOriginalPriceString,
    isMemberDiscount,
  };
};

export default usePdpOriginalPrice;
